/**
 * ytOAuth.js — YouTube OAuth2 client utility para Restreamer UI.
 *
 * Persistencia:
 *   - Credenciales (client_id, client_secret) y cuentas (tokens) se guardan
 *     en el servidor → /data/fb/config.json (volumen Docker persistente).
 *   - localStorage se usa solo como caché para render inmediato (sin round-trip).
 *
 * Estructura en config.json (gestionado por server/index.js):
 *   __yt_config: { client_id, client_secret (cifrado AES-256-GCM) }
 *   yt__<channel_id>: {
 *     account_key, label, channel_title, channel_id,
 *     access_token  (cifrado), refresh_token (cifrado),
 *     token_expiry (Unix ms), updated_at
 *   }
 */

const LS_KEY = '@@restreamer-ui@@yt_oauth_v2';

// Mismo base URL que fbOAuth — Caddy proxea /fb-server → Node :3002
const _rtCfg = (typeof window !== 'undefined' && window.__RESTREAMER_CONFIG__) || {};
const SERVER_BASE = _rtCfg.FB_SERVER_URL
	? _rtCfg.FB_SERVER_URL.replace(/\/$/, '')
	: '/fb-server';

// ── localStorage cache ────────────────────────────────────────────────────────
function _readCache() {
	try {
		const raw = localStorage.getItem(LS_KEY);
		return raw ? JSON.parse(raw) : { client_id: '', client_secret: '', accounts: {} };
	} catch {
		return { client_id: '', client_secret: '', accounts: {} };
	}
}

function _writeCache(data) {
	try { localStorage.setItem(LS_KEY, JSON.stringify(data)); }
	catch (_) {}
}

// ── Server helpers ────────────────────────────────────────────────────────────
async function _get(path) {
	const res = await fetch(SERVER_BASE + path);
	return res.json();
}

async function _put(path, body) {
	const res = await fetch(SERVER_BASE + path, {
		method: 'PUT', headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});
	return res.json();
}

async function _post(path, body) {
	const res = await fetch(SERVER_BASE + path, {
		method: 'POST', headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});
	return res.json();
}

async function _del(path) {
	const res = await fetch(SERVER_BASE + path, { method: 'DELETE' });
	return res.json();
}

// ── Credentials ───────────────────────────────────────────────────────────────

/** Guardar en localStorage (sync, para compatibilidad legacy) */
const setCredentials = (client_id, client_secret) => {
	const cache = _readCache();
	cache.client_id = client_id || '';
	cache.client_secret = client_secret || '';
	_writeCache(cache);
};

/**
 * Guardar credenciales → servidor (config.json) + localStorage.
 * @returns {Promise<{ok: boolean}>}
 */
const saveCredentials = async (client_id, client_secret) => {
	setCredentials(client_id, client_secret); // caché inmediata
	try {
		return await _put('/yt/config', { client_id: client_id || '', client_secret: client_secret || '' });
	} catch (err) {
		console.warn('[ytOAuth] saveCredentials server error:', err.message);
		return { ok: false, error: err.message };
	}
};

/** Obtener credenciales desde localStorage (sync) */
const getCredentials = () => {
	const cache = _readCache();
	return { client_id: cache.client_id || '', client_secret: cache.client_secret || '' };
};

/**
 * Sincronizar credenciales desde el servidor → localStorage.
 * Llamar al montar Settings para cargar lo que está en config.json.
 */
const loadCredentialsFromServer = async () => {
	try {
		// /yt/config/full incluye el client_secret descifrado
		const data = await _get('/yt/config/full');
		if (data.client_id) {
			setCredentials(data.client_id, data.client_secret || '');
		}
		return { client_id: data.client_id || '', client_secret: data.client_secret || '' };
	} catch (_) {
		return getCredentials();
	}
};

// ── Accounts ──────────────────────────────────────────────────────────────────

/**
 * Guardar cuenta en servidor (persiste en config.json cifrado) + caché.
 */
const saveAccount = async (accountKey, accountData) => {
	// 1. Actualizar caché inmediatamente
	const cache = _readCache();
	if (!cache.accounts) cache.accounts = {};
	cache.accounts[accountKey] = { ...(cache.accounts[accountKey] || {}), ...accountData, updated_at: Date.now() };
	_writeCache(cache);

	// 2. Persistir en servidor
	try {
		return await _post('/yt/accounts', {
			account_key:               accountKey,
			label:                     accountData.label                     || accountData.channel_title || accountKey,
			channel_title:             accountData.channel_title             || '',
			channel_id:                accountData.channel_id                || '',
			access_token:              accountData.access_token              || '',
			refresh_token:             accountData.refresh_token             || '',
			token_expiry:              accountData.token_expiry              || 0,
			restreamer_channel_id:     accountData.restreamer_channel_id     || '',
			restreamer_publication_id: accountData.restreamer_publication_id || '',
		});
	} catch (err) {
		console.warn('[ytOAuth] saveAccount server error:', err.message);
		return { ok: false, error: err.message };
	}
};

/**
 * Guardar cuenta sync en localStorage (legacy — para flujos que no son async).
 */
const saveAccountSync = (accountKey, accountData) => {
	const cache = _readCache();
	if (!cache.accounts) cache.accounts = {};
	cache.accounts[accountKey] = { ...(cache.accounts[accountKey] || {}), ...accountData, updated_at: Date.now() };
	_writeCache(cache);
};

/**
 * Cargar lista de cuentas desde el servidor → actualiza caché.
 */
const loadAccountsFromServer = async () => {
	try {
		const accounts = await _get('/yt/accounts');
		if (Array.isArray(accounts)) {
			const cache = _readCache();
			// Convertir array a mapa { [account_key]: data }
			cache.accounts = {};
			accounts.forEach((a) => { cache.accounts[a.account_key] = a; });
			_writeCache(cache);
			return cache.accounts;
		}
	} catch (_) {}
	return _readCache().accounts || {};
};

/**
 * Obtener token completo (con access_token + refresh_token) desde el servidor.
 */
const getAccountToken = async (accountKey) => {
	try {
		return await _get(`/yt/accounts/${encodeURIComponent(accountKey)}/token`);
	} catch (_) {
		return getAccount(accountKey);
	}
};

/** Obtener todas las cuentas desde localStorage (sync) */
const getAccounts = () => {
	return _readCache().accounts || {};
};

/** Obtener una cuenta por key desde localStorage (sync) */
const getAccount = (accountKey) => {
	return (_readCache().accounts || {})[accountKey] || null;
};

/** Obtener todos los datos */
const getAll = () => _readCache();

/** Listar cuentas como array */
const listAccounts = () => {
	return Object.entries(_readCache().accounts || {}).map(([key, val]) => ({ key, ...val }));
};

/**
 * Eliminar cuenta del servidor + caché.
 */
const removeAccount = async (accountKey) => {
	// 1. Eliminar del caché
	const cache = _readCache();
	if (cache.accounts && cache.accounts[accountKey]) {
		delete cache.accounts[accountKey];
		_writeCache(cache);
	}
	// 2. Eliminar del servidor
	try {
		return await _del(`/yt/accounts/${encodeURIComponent(accountKey)}`);
	} catch (err) {
		console.warn('[ytOAuth] removeAccount server error:', err.message);
		return { ok: false };
	}
};

// ── Token management ──────────────────────────────────────────────────────────

/**
 * Refrescar access_token usando el refresh_token del servidor.
 * El servidor hace la llamada a Google y actualiza config.json.
 */
const refreshAccount = async (accountKey) => {
	try {
		const data = await _post(`/yt/accounts/${encodeURIComponent(accountKey)}/refresh`, {});
		if (data.error) throw new Error(data.error);
		// Actualizar caché
		const cache = _readCache();
		if (cache.accounts && cache.accounts[accountKey]) {
			cache.accounts[accountKey].access_token = data.access_token;
			cache.accounts[accountKey].token_expiry  = data.token_expiry;
			_writeCache(cache);
		}
		return data.access_token;
	} catch (err) {
		// Fallback: intentar con credenciales locales
		const creds   = getCredentials();
		const account = getAccount(accountKey);
		if (!account || !account.refresh_token) throw new Error('No refresh token for account: ' + accountKey);
		if (!creds.client_id || !creds.client_secret) throw new Error('Global OAuth2 credentials not set');

		const resp = await fetch('https://oauth2.googleapis.com/token', {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: new URLSearchParams({
				client_id:     creds.client_id,
				client_secret: creds.client_secret,
				refresh_token: account.refresh_token,
				grant_type:    'refresh_token',
			}).toString(),
		});
		const tokenData = await resp.json();
		if (tokenData.error) throw new Error(tokenData.error_description || tokenData.error);

		// Persistir nuevo access_token
		await saveAccount(accountKey, {
			access_token: tokenData.access_token,
			token_expiry: Date.now() + (tokenData.expires_in || 3600) * 1000,
		});
		return tokenData.access_token;
	}
};

/** Obtener token válido (refresca automáticamente si está expirado).
 *  Si el access_token no está en caché local (porque viene del servidor cifrado),
 *  lo obtiene del endpoint /yt/accounts/:key/token.
 */
const getValidToken = async (accountKey) => {
	let account = getAccount(accountKey);
	if (!account) throw new Error('Account not connected: ' + accountKey);

	// Si no hay access_token en caché local, obtenerlo del servidor
	if (!account.access_token) {
		const serverAccount = await getAccountToken(accountKey);
		if (serverAccount && serverAccount.access_token) {
			// Guardar en caché para futuras llamadas
			const cache = _readCache();
			if (!cache.accounts) cache.accounts = {};
			cache.accounts[accountKey] = { ...(cache.accounts[accountKey] || {}), ...serverAccount };
			_writeCache(cache);
			account = cache.accounts[accountKey];
		} else {
			throw new Error('Could not retrieve token for account: ' + accountKey);
		}
	}

	const isExpired = account.token_expiry && Date.now() > account.token_expiry - 60000;
	if (isExpired) return await refreshAccount(accountKey);
	return account.access_token;
};

/** Fetch info del canal de YouTube */
const fetchChannelInfo = async (accessToken) => {
	try {
		const resp = await fetch(
			'https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true',
			{ headers: { Authorization: 'Bearer ' + accessToken } }
		);
		const data = await resp.json();
		if (data.items && data.items.length > 0) {
			return {
				channel_title: data.items[0].snippet.title,
				channel_id:    data.items[0].id,
			};
		}
	} catch (_) {}
	return {};
};

export default {
	// Credentials
	setCredentials,
	saveCredentials,
	getCredentials,
	loadCredentialsFromServer,
	// Accounts
	getAll,
	saveAccount,
	saveAccountSync,
	loadAccountsFromServer,
	getAccountToken,
	getAccounts,
	getAccount,
	removeAccount,
	listAccounts,
	// Token management
	refreshAccount,
	getValidToken,
	// Google API
	fetchChannelInfo,
};

